import React, { useState } from "react";
import { Box, Tabs, useMediaQuery, useTheme } from "@mui/material";
import { usePageTitle } from "hooks/usePageTitle";
import { NAVBAR_HEIGHT } from "constants/layout";
import CustomTab from "components/CustomTab";
import { a11yProps, CustomTabPanel } from "components/CustomTabPanel";
import LogsViewerContent from "scenes/logs/LogsViewerContent";
import ServerOverview from "scenes/server/ServerOverview";
import ServerHistory from "scenes/server/ServerHistory";
import ServerDatabase from "scenes/server/ServerDatabase";
import ServerSetup from "scenes/server/ServerSetup";

/**
 * Server page (2026-10-21).
 *
 * Replaces the old "Logs" sidebar entry and keeps the log viewer as its own
 * tab. Every other tab reads snapshots written by the system-cron collector —
 * the web process never shells out for metrics.
 *
 * Tabs:
 *   Stats    — current CPU/RAM/disk/services/SSL/backups + health banner
 *   History  — 1h / 6h / 24h / 7d charts
 *   Database — table row counts, sizes, pool, collations
 *   Logs     — the original log viewer (unchanged)
 *   Setup    — is the collector running, and what is monitored
 *
 * Admin only — the route guard lives in App.js.
 */
const Server = () => {
  usePageTitle("Server");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const [tabValue, setTabValue] = useState(0);

  const handleTabsChange = (_event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box
      p="1rem 1rem 0 1rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        "@supports (height: 100dvh)": {
          height: `calc(100dvh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        },
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {/* Sticky tabs — matches the other list-screen headers */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
          bgcolor: theme.palette.background.alt,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabsChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            "& .MuiTab-root": { borderRadius: "12px !important" },
            "& .MuiTab-root.Mui-selected": { borderRadius: "12px !important" },
          }}
        >
          <CustomTab label="Stats" {...a11yProps(0)} />
          <CustomTab label="History" {...a11yProps(1)} />
          <CustomTab label="Database" {...a11yProps(2)} />
          <CustomTab label="Logs" {...a11yProps(3)} />
          <CustomTab label="Setup" {...a11yProps(4)} />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pb: 1 }}>
        <CustomTabPanel value={tabValue} index={0} sx={{ p: 0 }}>
          <ServerOverview />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={1} sx={{ p: 0 }}>
          <ServerHistory />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={2} sx={{ p: 0 }}>
          <ServerDatabase />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={3} sx={{ p: 0 }}>
          <LogsViewerContent />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={4} sx={{ p: 0 }}>
          <ServerSetup />
        </CustomTabPanel>
      </Box>
    </Box>
  );
};

export default Server;
